import { useState } from "react";
import {
  useRecommendationPanel,
  type RecommendationPanelRenderers,
  type RecommendationSubmissionProps,
  extractImageUrls,
  extractText,
  getRecommendationSubmission,
  parseRecommendationFields,
} from "@launch10/use-langgraph-stream";
import { recommendationById } from "~/lib/recommendationsRegistry";
import type { RecommendationComponent } from "@shared";
import {
  ArrowLeftRight,
  BarChart3,
  CalendarClock,
  Award,
  BadgeCheck,
  Building2,
  ChartColumn,
  ChartLine,
  CircleDollarSign,
  CircleHelp,
  CreditCard,
  FileText,
  FlaskConical,
  GitCompare,
  Globe,
  Image as ImageIcon,
  Inbox,
  LayoutGrid,
  Lightbulb,
  Link as LinkIcon,
  ListChecks,
  ListOrdered,
  ListX,
  Lock,
  Magnet,
  Mail,
  MapPin,
  Megaphone,
  Package,
  Palette,
  PenLine,
  PencilLine,
  Phone,
  Plus,
  Quote,
  Rocket,
  Search,
  Shapes,
  Sparkles,
  Star,
  Target,
  Timer,
  Trash2,
  TrendingDown,
  Type,
  Users,
  Utensils,
  Video,
  X,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";
import type { Recommendation } from "@launch10/use-langgraph-stream";
import {
  DomainPicker,
  type DomainAssignment,
} from "@components/website/domain-picker/DomainPicker";
import { PagePicker } from "@components/website/preview";
import { useWebsiteId } from "~/stores/projectStore";
import { useWebsitePages } from "@api/websites.hooks";

/**
 * RECOMMENDATION_PANELS — the app's injected mapping for the chat widget's
 * recommendation system (use-langgraph-stream owns the chips row, the
 * composer transform, the panel chrome, and submit assembly; we own only
 * what renders inside).
 *
 * Keys are bespoke component names (shared RECOMMENDATION_COMPONENTS) or
 * recommendationIds (chrome — icon/description — for generic field panels).
 * `Submission` entries are the transcript anchors: a submitted panel
 * re-renders as its structured card straight from the HumanMessage's
 * metadata (recommendation_id / component / collected) — from history too.
 */

// ─────────────────────────────────────────────────────────────────────────
// DomainPicker — connect-domain (custom IO: availability checks)
// ─────────────────────────────────────────────────────────────────────────

function DomainPickerPanel() {
  const { setField } = useRecommendationPanel();
  const [selection, setSelection] = useState<DomainAssignment | null>(null);

  return (
    <DomainPicker
      selection={selection}
      onSelectionChange={(next) => {
        setSelection(next);
        setField("domain_name", next?.domain ?? "");
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ColorPicker — change-colors (presets + custom hexes + describe-the-vibe)
// ─────────────────────────────────────────────────────────────────────────

const COLOR_PRESETS = [
  { name: "Warm", colors: ["#b45a3c", "#e8833a", "#e3c567"] },
  { name: "Ocean", colors: ["#1e3a5f", "#2a9d8f", "#a8dadc"] },
  { name: "Mono", colors: ["#111111", "#6b7280", "#e5e7eb"] },
  { name: "Berry", colors: ["#4a1942", "#a8326e", "#e84393"] },
] as const;

function ColorPickerPanel() {
  const { collected, setField } = useRecommendationPanel();
  const picked = typeof collected.direction === "string" ? collected.direction : "";

  return (
    <div className="space-y-4">
      <div>
        <span className="flex items-baseline gap-1.5 text-sm">
          <span className="font-medium text-neutral-800">Pick a direction</span>
          <span className="text-xs text-neutral-400">optional</span>
        </span>
        <div className="mt-2 flex flex-wrap gap-3">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => setField("direction", picked === preset.name ? "" : preset.name)}
              className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 transition-colors ${
                picked === preset.name
                  ? "border-primary-300 bg-primary-50"
                  : "border-neutral-200 bg-white hover:border-neutral-300"
              }`}
            >
              <span className="flex gap-1">
                {preset.colors.map((color) => (
                  <span
                    key={color}
                    className="size-7 rounded-md"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </span>
              <span className="text-xs text-neutral-600">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>
      <label className="block text-sm">
        <span className="flex items-baseline gap-1.5">
          <span className="font-medium text-neutral-800">Or describe it</span>
          <span className="text-xs text-neutral-400">optional</span>
        </span>
        <input
          type="text"
          aria-label="Describe the color direction"
          placeholder="warm, earthy, premium…"
          value={typeof collected.description === "string" ? collected.description : ""}
          onChange={(e) => setField("description", e.target.value)}
          className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
        />
      </label>
    </div>
  );
}

function ColorPickerSubmission({ collected }: RecommendationSubmissionProps) {
  const direction = typeof collected.direction === "string" ? collected.direction : "";
  const preset = COLOR_PRESETS.find((p) => p.name === direction);
  const description = typeof collected.description === "string" ? collected.description : "";

  return (
    <SubmissionCard icon={<Palette />} title="Change my colors">
      {preset && (
        <span className="inline-flex items-center gap-1.5">
          <span className="flex gap-1">
            {preset.colors.map((color) => (
              <span key={color} className="size-4 rounded" style={{ backgroundColor: color }} />
            ))}
          </span>
          <span>{preset.name}</span>
        </span>
      )}
      {description && <p className="text-neutral-600">“{description}”</p>}
    </SubmissionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Testimonials — repeatable {quote, author} cards with a headshot drop each
// ─────────────────────────────────────────────────────────────────────────

// A `type` (not interface) so it picks up the implicit index signature and
// stays assignable to the library's CollectedValue record arrays.
type TestimonialDraft = {
  quote: string;
  author: string;
};

function TestimonialsPanel() {
  const { collected, setField, addImages, attachmentsFor, removeAttachment } =
    useRecommendationPanel();
  const items: TestimonialDraft[] = Array.isArray(collected.testimonials)
    ? (collected.testimonials as TestimonialDraft[])
    : [{ quote: "", author: "" }];

  const update = (index: number, patch: Partial<TestimonialDraft>) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    setField("testimonials", next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const headshotField = `headshot_${index + 1}`;
        const headshots = attachmentsFor(headshotField);
        return (
          <div
            key={index}
            className="flex gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3"
          >
            {/* Headshot drop square — uploads ride the chat composer, tagged */}
            <label className="flex size-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-neutral-300 bg-white text-neutral-400 hover:border-neutral-400">
              {headshots[0]?.url || headshots[0]?.previewUrl ? (
                <img
                  src={headshots[0].url ?? headshots[0].previewUrl}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                <ImageIcon className="size-5" />
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                aria-label={`Headshot ${index + 1}`}
                onChange={(e) => {
                  if (e.target.files?.length) {
                    // One headshot per testimonial: replace, don't accumulate.
                    headshots.forEach((a) => removeAttachment(a.id));
                    addImages(headshotField, [e.target.files[0]], `Headshot ${index + 1}`);
                  }
                  e.target.value = "";
                }}
              />
            </label>

            <div className="min-w-0 flex-1 space-y-2">
              <textarea
                aria-label={`Testimonial ${index + 1} quote`}
                placeholder="“They booked us solid for three months…”"
                value={item.quote}
                onChange={(e) => update(index, { quote: e.target.value })}
                rows={2}
                className="w-full resize-none rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
              />
              <input
                type="text"
                aria-label={`Testimonial ${index + 1} author`}
                placeholder="Name, role"
                value={item.author}
                onChange={(e) => update(index, { author: e.target.value })}
                className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
              />
            </div>

            {items.length > 1 && (
              <button
                type="button"
                aria-label={`Remove testimonial ${index + 1}`}
                onClick={() => {
                  attachmentsFor(headshotField).forEach((a) => removeAttachment(a.id));
                  setField("testimonials", items.filter((_, i) => i !== index));
                }}
                className="-mr-1.5 -mt-1.5 self-start rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => setField("testimonials", [...items, { quote: "", author: "" }])}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-100"
      >
        <Plus className="size-4" />
        Add testimonial
      </button>
    </div>
  );
}

function TestimonialsSubmission({ message, collected }: RecommendationSubmissionProps) {
  const items: TestimonialDraft[] = Array.isArray(collected.testimonials)
    ? (collected.testimonials as TestimonialDraft[])
    : [];
  const images = extractImageUrls(
    (message as { content?: Parameters<typeof extractImageUrls>[0] }).content,
  );

  return (
    <SubmissionCard icon={<Quote />} title="Add testimonials">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-2">
          {images[index] && (
            <img src={images[index]} alt="" className="size-8 shrink-0 rounded-full object-cover" />
          )}
          <div className="min-w-0">
            <p className="text-neutral-700">“{item.quote}”</p>
            {item.author && <p className="text-xs text-neutral-400">— {item.author}</p>}
          </div>
        </div>
      ))}
    </SubmissionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// GenericSubmission — the default transcript card for any recommendation
// activation without a bespoke Submission renderer. Panel submissions
// mirror the auto-generated field panel (the registry entry supplies
// icon/title/field prompts; the message supplies values, note, images).
// A prompt chip's send has no fields: the card shows the SENT text — the
// engineered prompt under the chip's title, so the user sees the real ask
// that went out on their behalf.
// ─────────────────────────────────────────────────────────────────────────

function fieldValueText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    // String lists join inline; a group field's records render one per line.
    if (value.every((v) => typeof v === "string")) return value.join(", ");
    return value.map((v) => fieldValueText(v)).join("\n");
  }
  if (value && typeof value === "object") {
    return Object.values(value)
      .filter((v): v is string => typeof v === "string" && v.trim() !== "")
      .join(" · ");
  }
  return String(value ?? "");
}

export function GenericSubmission({
  message,
  recommendationId,
  collected,
}: RecommendationSubmissionProps) {
  const entry = recommendationById(recommendationId);
  const note = getRecommendationSubmission(message)?.note ?? null;
  const allImages = extractImageUrls(
    (message as { content?: Parameters<typeof extractImageUrls>[0] }).content,
  );

  // The same field specs the panel rendered: prompt per field, in order.
  const specs = parseRecommendationFields(entry?.collects);
  const specByName = new Map(specs.map((f) => [f.name, f]));

  // Image fields: their collected value IS urls — render thumbnails under
  // the field's prompt, never the raw url text.
  const urlsOf = (value: unknown): string[] =>
    typeof value === "string" ? [value] : Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

  const imageRows: { label: string; urls: string[] }[] = [];
  const textRows: { label: string; value: string }[] = [];
  for (const [field, value] of Object.entries(collected)) {
    const spec = specByName.get(field);
    const label = spec?.prompt ?? field.replace(/_/g, " ");
    if (spec?.accepts === "image") {
      const urls = urlsOf(value);
      if (urls.length) imageRows.push({ label, urls });
      continue;
    }
    const text = fieldValueText(value);
    if (text.trim() !== "") textRows.push({ label, value: text });
  }

  // Whatever the fields didn't claim is the note's own attachments.
  const fieldUrls = new Set(imageRows.flatMap((row) => row.urls));
  const noteImages = allImages.filter((url) => !fieldUrls.has(url));

  // A prompt chip's send carries no fields — the body IS the sent text
  // (the engineered prompt, however the user edited it).
  const sentText =
    textRows.length === 0 && imageRows.length === 0 && !note
      ? extractText(
          (message as { content?: Parameters<typeof extractText>[0] }).content,
        ).trim()
      : "";

  return (
    <SubmissionCard
      icon={entry ? recommendationIconFor(entry) : <Plus />}
      title={entry?.text ?? recommendationId}
    >
      {sentText && (
        <p className="whitespace-pre-line break-words">{sentText}</p>
      )}
      {textRows.map((row) => (
        <div key={row.label} className="flex items-baseline gap-1.5">
          <span className="shrink-0">{row.label}</span>
          <span className="min-w-0 whitespace-pre-line break-words font-semibold">
            {row.value}
          </span>
        </div>
      ))}
      {imageRows.map((row) => (
        <div key={row.label} className="space-y-1">
          {/* The prompt is panel-time language ("Drop the photos…") — in the
              transcript, thumbnails speak for themselves. Labels only when
              multiple image fields need distinguishing (before/after). */}
          {imageRows.length > 1 && <span>{row.label}</span>}
          <div className="flex flex-wrap gap-1.5">
            {row.urls.map((url) => (
              <img key={url} src={url} alt="" className="size-12 rounded-md object-cover" />
            ))}
          </div>
        </div>
      ))}
      {noteImages.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {noteImages.map((url) => (
            <img key={url} src={url} alt="" className="size-10 rounded-md object-cover" />
          ))}
        </div>
      )}
      {note && <p className="italic text-neutral-700">“{note}”</p>}
    </SubmissionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SubmissionCard — the shared transcript-anchor shell (right-aligned, ours)
// ─────────────────────────────────────────────────────────────────────────

function SubmissionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-end" data-testid="recommendation-submission">
      <div className="max-w-[85%] space-y-2 rounded-2xl rounded-br-md border border-primary-100 bg-primary-50 p-3 text-sm text-neutral-900">
        <div className="flex items-center gap-1.5 font-medium text-primary-700 [&>svg]:size-3.5">
          {icon}
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// The mapping
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// Chip icons — registry `icon:` names (lucide kebab-case) → rendered nodes
// ─────────────────────────────────────────────────────────────────────────

const CHIP_ICONS: Record<string, ReactNode> = {
  "arrow-left-right": <ArrowLeftRight />,
  award: <Award />,
  "badge-check": <BadgeCheck />,
  "building-2": <Building2 />,
  "calendar-clock": <CalendarClock />,
  "chart-column": <ChartColumn />,
  "chart-line": <ChartLine />,
  "circle-dollar-sign": <CircleDollarSign />,
  "circle-help": <CircleHelp />,
  "credit-card": <CreditCard />,
  "file-text": <FileText />,
  "flask-conical": <FlaskConical />,
  "git-compare": <GitCompare />,
  globe: <Globe />,
  image: <ImageIcon />,
  inbox: <Inbox />,
  "layout-grid": <LayoutGrid />,
  lightbulb: <Lightbulb />,
  link: <LinkIcon />,
  "list-checks": <ListChecks />,
  "list-ordered": <ListOrdered />,
  "list-x": <ListX />,
  lock: <Lock />,
  magnet: <Magnet />,
  mail: <Mail />,
  "map-pin": <MapPin />,
  megaphone: <Megaphone />,
  package: <Package />,
  palette: <Palette />,
  "pen-line": <PenLine />,
  "pencil-line": <PencilLine />,
  phone: <Phone />,
  plus: <Plus />,
  quote: <Quote />,
  rocket: <Rocket />,
  search: <Search />,
  shapes: <Shapes />,
  sparkles: <Sparkles />,
  star: <Star />,
  target: <Target />,
  timer: <Timer />,
  "trash-2": <Trash2 />,
  "trending-down": <TrendingDown />,
  type: <Type />,
  users: <Users />,
  utensils: <Utensils />,
  zap: <Zap />,
};

/**
 * Resolve a chip's icon from its registry-declared name; every chip gets
 * one (mock parity), so unmapped/missing names fall back to a plus.
 */
export function recommendationIconFor(rec: Recommendation): ReactNode {
  return (rec.icon && CHIP_ICONS[rec.icon]) || <Plus />;
}

// ─────────────────────────────────────────────────────────────────────────
// LeadMagnetForm — create-a-lead-magnet (kind toggle, title→slug, the asset).
// The PDF rides the chat composer like a headshot (addImages → addFiles); its
// upload_id reaches the agent on the submission sidecar. Link/video carry a
// url. The agent runs the lead-magnet SOP to create + wire <LeadForm.Download>.
// ─────────────────────────────────────────────────────────────────────────

const MAGNET_KINDS = [
  { value: "pdf", label: "PDF", icon: <FileText /> },
  { value: "link", label: "Link", icon: <LinkIcon /> },
  { value: "video", label: "Video", icon: <Video /> },
] as const;

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

// A live preview of the slug the agent freezes server-side from the title.
function leadMagnetSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const NEW_PAGE = "__new__";

function LeadMagnetFormPanel() {
  const { collected, setField, addImages, attachmentsFor, removeAttachment } =
    useRecommendationPanel();
  const websiteId = useWebsiteId();
  const { data: pages = [] } = useWebsitePages(websiteId ?? undefined);
  const kind = asString(collected.kind) || "pdf";
  const title = asString(collected.title);
  const slug = leadMagnetSlug(title);
  const file = attachmentsFor("file")[0];
  // Default to a new page — we make one per magnet to track conversions. An
  // existing page already has its content, so we skip headline/subheadline.
  const page = asString(collected.page) || NEW_PAGE;
  const isNewPage = page === NEW_PAGE;

  const pickKind = (next: string) => {
    setField("kind", next);
    // Keep one asset shape on the submission: drop the other kind's input.
    if (next === "pdf") {
      setField("url", "");
    } else {
      attachmentsFor("file").forEach((a) => removeAttachment(a.id));
      setField("asset", "");
    }
  };

  // The PDF rides the chat composer, tagged "file" so it fills this slot
  // (not the untagged note area). `asset` mirrors the name for isSubmittable.
  const addPdf = (picked?: File | null) => {
    if (!picked) return;
    addImages("file", [picked], "PDF");
    setField("kind", "pdf");
    setField("asset", picked.name);
  };

  return (
    <div className="space-y-4">
      <div>
        <span className="block text-sm font-medium text-neutral-800">Which page should this live on?</span>
        <PagePicker
          className="mt-2 w-full"
          pages={pages}
          value={page}
          onChange={(v) => setField("page", v)}
          leadingOption={{
            value: NEW_PAGE,
            label: "Create a new page for this lead magnet",
            sublabel: "Recommended — a dedicated page tracks conversions",
          }}
          testIdPrefix="lead-magnet-page"
        />
      </div>

      <div>
        <span className="block text-sm font-medium text-neutral-800">What are you offering?</span>
        <div className="mt-2 inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-1">
          {MAGNET_KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => pickKind(k.value)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors [&>svg]:size-4 ${
                kind === k.value
                  ? "bg-white font-medium text-neutral-900 shadow-sm"
                  : "text-neutral-500 hover:text-neutral-700"
              }`}
            >
              {k.icon}
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {isNewPage && (
        <>
      <label className="block text-sm">
        <span className="font-medium text-neutral-800">What headline should we use on this page?</span>
        <input
          type="text"
          aria-label="Lead magnet title"
          placeholder="Free SEO Checklist"
          value={title}
          onChange={(e) => setField("title", e.target.value)}
          className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
        />
        {slug && (
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-neutral-400">
            <LinkIcon className="size-3 shrink-0" />
            We'll create a page at
            <code className="rounded bg-primary-50 px-1.5 py-0.5 font-mono text-primary-600">
              /{slug}
            </code>
            to track conversions
          </span>
        )}
      </label>

      <label className="block text-sm">
        <span className="flex items-baseline gap-1.5">
          <span className="font-medium text-neutral-800">Subheadline</span>
          <span className="text-xs text-neutral-400">optional</span>
        </span>
        <input
          type="text"
          aria-label="Subheadline"
          placeholder="One line on what they'll get"
          value={asString(collected.subheadline)}
          onChange={(e) => setField("subheadline", e.target.value)}
          className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
        />
      </label>
        </>
      )}

      {kind === "pdf" ? (
        <div className="text-sm">
          <span className="font-medium text-neutral-800">The file to unlock</span>
          {file ? (
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
              <FileText className="size-4 shrink-0 text-neutral-400" />
              <span className="min-w-0 flex-1 truncate text-neutral-700">
                {file.filename ?? "Your PDF"}
              </span>
              <button
                type="button"
                aria-label="Remove file"
                onClick={() => {
                  removeAttachment(file.id);
                  setField("asset", "");
                }}
                className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : (
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                // Capture the drop here (tagged "file") so it fills this slot —
                // otherwise the panel-wide drop zone grabs it untagged and it
                // lands in the note area, leaving this looking empty.
                e.preventDefault();
                e.stopPropagation();
                addPdf(e.dataTransfer.files?.[0]);
              }}
              className="mt-1 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-8 text-center hover:border-neutral-400"
            >
              <FileText className="size-6 text-neutral-400" />
              <span className="text-neutral-600">Drag your PDF here</span>
              <span className="text-xs text-neutral-400">
                or click to browse · unlocked after they enter an email
              </span>
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                aria-label="The file to unlock"
                onChange={(e) => {
                  addPdf(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          )}
        </div>
      ) : (
        <label className="block text-sm">
          <span className="font-medium text-neutral-800">
            {kind === "video" ? "Video URL" : "Link URL"}
          </span>
          <input
            type="url"
            aria-label={kind === "video" ? "Video URL" : "Link URL"}
            placeholder={kind === "video" ? "https://youtube.com/watch?v=…" : "https://…"}
            value={asString(collected.url)}
            onChange={(e) => setField("url", e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
          />
        </label>
      )}
    </div>
  );
}

function LeadMagnetFormSubmission({ collected }: RecommendationSubmissionProps) {
  const kind = asString(collected.kind) || "pdf";
  const title = asString(collected.title);
  const detail = kind === "pdf" ? asString(collected.asset) || "PDF" : asString(collected.url);
  const pageValue = asString(collected.page);
  const isNew = !pageValue || pageValue === NEW_PAGE;
  const pageLabel = isNew ? `New page · /${leadMagnetSlug(title)}` : `On ${pageValue}`;
  return (
    <SubmissionCard icon={<Magnet />} title="Create a lead magnet">
      {title && <p className="font-semibold">{title}</p>}
      {detail && <p className="text-neutral-600">{detail}</p>}
      <p className="text-xs text-neutral-400">{pageLabel}</p>
    </SubmissionCard>
  );
}

/** Bespoke panels — keyed by the shared RECOMMENDATION_COMPONENTS names. */
const BESPOKE_PANELS: Record<
  RecommendationComponent,
  RecommendationPanelRenderers[string]
> = {
  DomainPicker: {
    Panel: DomainPickerPanel,
    icon: <Globe />,
    isSubmittable: (collected) =>
      typeof collected.domain_name === "string" && collected.domain_name.length > 0,
  },
  ColorPicker: {
    Panel: ColorPickerPanel,
    Submission: ColorPickerSubmission,
    icon: <Palette />,
  },
  Testimonials: {
    Panel: TestimonialsPanel,
    Submission: TestimonialsSubmission,
    icon: <Quote />,
    isSubmittable: (collected) =>
      Array.isArray(collected.testimonials) &&
      (collected.testimonials as TestimonialDraft[]).some((t) => t.quote?.trim()),
  },
  LeadMagnetForm: {
    Panel: LeadMagnetFormPanel,
    Submission: LeadMagnetFormSubmission,
    icon: <Magnet />,
    submitLabel: "Create magnet",
    notePlaceholder: "Anything else you want to say on this lead magnet page?",
    isSubmittable: (collected) => {
      const has = (k: string) =>
        typeof collected[k] === "string" && (collected[k] as string).trim() !== "";
      // A new page needs a headline; an existing page already has its content.
      const isNew = !has("page") || collected.page === NEW_PAGE;
      if (isNew && !has("title")) return false;
      const kind = typeof collected.kind === "string" ? collected.kind : "pdf";
      return kind === "pdf" ? has("asset") : has("url");
    },
  },
};

export const RECOMMENDATION_PANELS: RecommendationPanelRenderers = {
  ...BESPOKE_PANELS,
  // Chrome for generic field panels, keyed by recommendationId.
  "website/gallery": {
    icon: <ImageIcon />,
  },
  "website/before-after": {
    icon: <ImageIcon />,
  },
  "website/stats-strip": {
    icon: <BarChart3 />,
  },
  "website/countdown-timer": {
    icon: <Timer />,
  },
  "website/calendly-booking": {
    icon: <CalendarClock />,
  },
  "website/about-team": {
    icon: <Users />,
  },
  "website/credentials": {
    icon: <BadgeCheck />,
  },
  "website/cta-section": {
    icon: <Plus />,
  },
  "website/delete-page": {
    icon: <Trash2 />,
  },
  "website/faq": {
    icon: <CircleHelp />,
  },
  "website/how-it-works": {
    icon: <ListOrdered />,
  },
  "website/improve-copy": {
    icon: <Zap />,
  },
  "website/lead-form": {
    icon: <Inbox />,
  },
  "website/pick-icons": {
    icon: <Shapes />,
  },
  "website/star-ratings": {
    icon: <Star />,
  },
  "website/trust-badges": {
    icon: <BadgeCheck />,
  },
  "website/pricing-table": {
    icon: <CircleDollarSign />,
  },
  "website/press-awards": {
    icon: <Award />,
  },
};
